'use client'

import { Tabs, TabsProps, TimePicker, Tooltip } from 'antd';
import React, { useEffect, useRef, useState } from 'react'
import { formatMsg, Helper, wrapperComponents, attendanceActions, packageActions, multicenterActions, timeutil, AbsentNoteModal, notification, accountActions, SpinWrapper, permissions } from 'sharedresources'
import EntityAttendance from './EntityAttendance/page';
import SlotTimerPill from './SlotTimerPill';

import styles from './style.module.css';
import { useDispatch } from 'react-redux';
import { AttendanceBulkType, AttendanceEntityType, AttendanceOperationType, AttendanceStatus } from 'sharedresources/src/types/attendance';
import { useForm } from 'antd/es/form/Form';
import './style.css';


const { PageHeaderWrapper, Box } = wrapperComponents;

const { getParticipantsDateRangeAttendance, getInstructorsDateRangeAttendance, resetAttendanceReducer, defaultAttendanceReducerState, getAllEventsOfParticipants, getAllEventsOfInstructors, markMultipleEventsBulkAttendance, resetOperationType, getParticipantsDateRangeAttendanceByAccountId } = attendanceActions;
const { getAllPackageNames, getAllBatches, getPackageListDropdown } = packageActions;
const { getAllCentersList } = multicenterActions;
const { getUsersOfAccount } = accountActions

const Attendance = () => {

    const dispatch = useDispatch();
    const [form] = useForm();
    const {
        firebase,
        attendanceReducer,
        accountReducer,
        userReducer,
        packageReducer,
        multicenterReducer
    } = Helper.getSelectorState(['attendanceReducer', 'accountReducer', 'userReducer', 'packageReducer', 'multicenterReducer']);

    const {
        participantsAttendanceChannel,
        instructorsAttendanceChannel,
        participantsAttendanceMap,
        instructorsAttendanceMap,
        allInstructorsEvents,
        allParticipantsEvents
    } = attendanceReducer;

    const isTeacherLoggedIn = Helper.getLoggedInUserType(firebase).toLowerCase() === "teacher";
    // Slot timer is an admin-only aid; the users app renders this same component.
    const showSlotTimer = isTeacherLoggedIn && process.env.NEXT_PUBLIC_WORKSPACE_NAME === "admin";

    const teachersList = isTeacherLoggedIn ? Helper.getTeacherList(firebase) : null;

    const tabParam = Helper.getUrlParam("tab");

    const [activeTab, setActivetab] = useState(
        ['INSTRUCTOR', 'PARTICIPANT'].includes(tabParam)
            ? tabParam
            : 'PARTICIPANT'
    );

    const [instructorDateRange, setInstructorDateRange] = useState([
        timeutil.getInstance().startOf("day").valueOf(),
        timeutil.getInstance().endOf("day").valueOf()
    ]);
    const [participantDateRange, setParticipantDateRange] = useState([
        timeutil.getInstance().startOf("day").valueOf(),
        timeutil.getInstance().endOf("day").valueOf()
    ]);

    const [accountsMap, setAccountsMap] = useState(new Map());
    const [participantsMap, setParticipantsMap] = useState(new Map());
    const [centersMap, setCentersMap] = useState(new Map());
    const [packagesMap, setPackagesMap] = useState(new Map());
    const [batchesMap, setBatchesMap] = useState(new Map());
    const [teachersMap, setTeachersMap] = useState(new Map());


    const tempParticipantsAttendanceMapRef = useRef(new Map());
    const tempInstructorsAttendanceMapRef = useRef(new Map());

    const [instructorOperationType, setInstructorOperationType] = useState(undefined);
    const [showInstructorTimePicker, setShowInstructorTimePicker] = useState(undefined);

    const [participantOperationType, setParticipantOperationType] = useState(undefined);
    const [showParticipantTimePicker, setShowParticipantTimePicker] = useState(undefined);

    const [reRenderInstructor, setReRenderInstructor] = useState(false);
    const [reRenderParticipant, setReRenderParticipant] = useState(false);

    const [participantAttendanceSettings, setParticipantAttendanceSettings] = useState(undefined);
    const [instructorAttendanceSettings, setInstructorAttendanceSettings] = useState(undefined);

    const [instructorsBulkActionList, setInstructorBulkActionList] = useState(undefined);
    const [participantsBulkActionList, setParticipantBulkActionList] = useState(undefined);

    const [instructorBulkActionObj, setInstructorBulkActionObj] = useState(undefined);
    const [participantBulkActionObj, setParticipantBulkActionObj] = useState(undefined);

    const [isAbsentNoteModalOpen, setIsAbsentNoteModalOpen] = useState(false);
    const [absentNoteFor, setAbsentNoteFor] = useState(undefined);
    const [absentNoteEntityType, setAbsentNoteEntityType] = useState(undefined);

    const [instructorEditMode, setInstructorEditMode] = useState(false);
    const [participantEditMode, setParticipantEditMode] = useState(false);


    const [recordKey, setRecordKey] = useState(undefined);

    const instructorBulkModeOn = Boolean(instructorsBulkActionList?.length > 1);
    const participantBulkModeOn = Boolean(participantsBulkActionList?.length > 1);


    const canMarkStaffAttendance = firebase?.teacher?.superUser;

    useEffect(() => {

        closeAttendanceChannels();
        isTeacherLoggedIn
            ? dispatch(getAllPackageNames({ firebase }))
            : dispatch(getPackageListDropdown({ firebase, status: "ACTIVE" }));
        dispatch(getAllBatches({ firebase }));
        dispatch(getAllCentersList({ firebase }));
        return () => {
            dispatch(resetAttendanceReducer(defaultAttendanceReducerState));
        }
    }, []);

    useEffect(() => {
        if (!isTeacherLoggedIn) {
            dispatch(getUsersOfAccount({ firebase }));
        }
    }, [firebase])


    useEffect(() => {
        if (firebase) {
            setParticipantAttendanceSettings(firebase.schoolConfig?.studentAttendanceMode)
            setInstructorAttendanceSettings(firebase.schoolConfig?.staffAttendanceMode)
        }
    }, [firebase])

    useEffect(() => {
        if (firebase && !isTeacherLoggedIn && participantDateRange?.length > 0) {
            dispatch(getAllEventsOfParticipants({
                firebase,
                startDate: timeutil.getEpoch(participantDateRange[0]),
                endDate: timeutil.getEpoch(participantDateRange[1]),
                accountId: firebase.user.id
            }));
            dispatch(getParticipantsDateRangeAttendanceByAccountId({
                firebase,
                startDate: timeutil.getEpoch(participantDateRange[0]),
                endDate: timeutil.getEpoch(participantDateRange[1]),
                accountId: firebase.user.id
            }))
        }
    }, [firebase, participantDateRange])

    useEffect(() => {

        const newAccountsMap = new Map();
        Helper.getAccountList(firebase).forEach((accountObj) => {
            newAccountsMap.set(accountObj.id, accountObj);
        });
        setAccountsMap(newAccountsMap);

    }, [accountReducer.accountList]);

    useEffect(() => {

        const newParticipantsMap = new Map();
        const participantList = isTeacherLoggedIn ? Helper.getUserList(firebase) : accountReducer.accountUsersList;
        participantList.forEach((userObj) => {
            newParticipantsMap.set(userObj.id, userObj);
        });
        setParticipantsMap(newParticipantsMap);

    }, [userReducer.userList, accountReducer.accountUsersList]);

    useEffect(() => {

        const newCentersMap = new Map();
        (multicenterReducer.allCentersList || []).forEach((centerObj) => {
            newCentersMap.set(centerObj.id, centerObj);
        });
        setCentersMap(newCentersMap);

    }, [multicenterReducer.allCentersList]);

    useEffect(() => {
        const newPackagesMap = new Map();
        const packageList = isTeacherLoggedIn ? (packageReducer.packageNames || []) : (packageReducer.packageList || [])
        packageList.forEach((packageObj) => {
            newPackagesMap.set(packageObj.id, packageObj);
        });
        setPackagesMap(newPackagesMap);

    }, [packageReducer.packageNames, packageReducer.packageList]);

    useEffect(() => {
        const newBatchesMap = new Map();
        (packageReducer.batchList || []).forEach((batchObj) => {
            newBatchesMap.set(batchObj.id, batchObj);
        });
        setBatchesMap(newBatchesMap);

    }, [packageReducer.batchList]);

    useEffect(() => {

        const newTeachersMap = new Map();
        (teachersList || []).forEach((teacherObj) => {
            newTeachersMap.set(teacherObj.id, teacherObj);
        });
        setTeachersMap(newTeachersMap);

    }, [firebase]);




    // All attendance channel actions 
    useEffect(() => {
        return () => {
            closeParticipantsAttendanceChannel();
        }
    }, [participantsAttendanceChannel]);

    useEffect(() => {
        return () => {
            closeInstructorsAttendanceChannel();
        }
    }, [instructorsAttendanceChannel]);


    useEffect(() => {
        if (isTeacherLoggedIn) {
            switch (activeTab) {
                case 'INSTRUCTOR':
                    closeParticipantsAttendanceChannel();
                    if (instructorDateRange) {
                        getInstructorsAttendance(timeutil.getEpoch(instructorDateRange[0]), timeutil.getEpoch(instructorDateRange[1]));
                        getInstructorsEvents(timeutil.getEpoch(instructorDateRange[0]), timeutil.getEpoch(instructorDateRange[1]));
                    }
                    break;
                case 'PARTICIPANT':
                    closeInstructorsAttendanceChannel();
                    if (participantDateRange) {
                        getParticipantsAttendance(timeutil.getEpoch(participantDateRange[0]), timeutil.getEpoch(participantDateRange[1]));
                        getParticipantsEvents(timeutil.getEpoch(participantDateRange[0]), timeutil.getEpoch(participantDateRange[1]));
                    }
                    break;
            }
        }
    }, [activeTab, instructorDateRange, participantDateRange, isTeacherLoggedIn]);

    useEffect(() => {

        if (tempInstructorsAttendanceMapRef.current.size > 1) {
            setInstructorEditMode(true);
        }
        else setInstructorEditMode(false);

        if (tempParticipantsAttendanceMapRef.current.size > 1) {
            setParticipantEditMode(true);
        }
        else setParticipantEditMode(false);

    }, [reRenderInstructor, reRenderParticipant]);

    useEffect(() => {
        // "Clear all" or "1 instructor selected" case (bulk mode to non-bulk mode)
        if ((instructorsBulkActionList?.length || 0) <= 1) {
            setInstructorBulkActionObj(undefined);
            setInstructorOperationType(undefined);
            setShowInstructorTimePicker(undefined);
        }
        // "Selected all" or "2 instructors selected" case (non-bulk mode to bulk mode)
        else if (
            instructorsBulkActionList?.length === 2 ||
            instructorsBulkActionList?.length === allInstructorsEvents.length
        ) {
            setInstructorOperationType(undefined);
            setShowInstructorTimePicker(undefined);
        }
    }, [instructorsBulkActionList]);

    useEffect(() => {
        // "Clear all" or "1 participant selected" case (bulk mode to non-bulk mode)
        if ((participantsBulkActionList?.length || 0) <= 1) {
            setParticipantBulkActionObj(undefined);
            setParticipantOperationType(undefined);
            setShowParticipantTimePicker(undefined);
        }
        // "Selected all" or "2 participants selected" case (non-bulk mode to bulk mode)
        else if (
            participantsBulkActionList?.length === 2 ||
            participantsBulkActionList?.length === allParticipantsEvents?.length
        ) {
            setParticipantOperationType(undefined);
            setShowParticipantTimePicker(undefined);
        }
    }, [participantsBulkActionList]);

    useEffect(() => {
        switch (attendanceReducer.operationType) {
            case 'BULK_ATTENDANCE_MARKED_SUCCESSFULLY':
                switch (activeTab) {
                    case 'INSTRUCTOR':
                        closeInstructorsAttendanceChannel();
                        getInstructorsAttendance(timeutil.getEpoch(instructorDateRange[0]), timeutil.getEpoch(instructorDateRange[1]));
                        getInstructorsEvents(timeutil.getEpoch(instructorDateRange[0]), timeutil.getEpoch(instructorDateRange[1]));
                        reRenderEntity("INSTRUCTOR");
                        break;
                    case 'PARTICIPANT':
                        closeParticipantsAttendanceChannel();
                        getParticipantsAttendance(timeutil.getEpoch(participantDateRange[0]), timeutil.getEpoch(participantDateRange[1]));
                        getParticipantsEvents(timeutil.getEpoch(participantDateRange[0]), timeutil.getEpoch(participantDateRange[1]));
                        reRenderEntity("PARTICIPANT");
                        break;
                }
                resetAllLocalStates(activeTab);
                dispatch(resetOperationType(null));
                break;
            case 'BULK_ATTENDANCE_MARKING_FAILED':
                switch (activeTab) {
                    case 'INSTRUCTOR':
                        setInstructorEditMode(true);
                        closeInstructorsAttendanceChannel();
                        getInstructorsAttendance(timeutil.getEpoch(instructorDateRange[0]), timeutil.getEpoch(instructorDateRange[1]));
                        getInstructorsEvents(timeutil.getEpoch(instructorDateRange[0]), timeutil.getEpoch(instructorDateRange[1]));
                        reRenderEntity("INSTRUCTOR");
                        break;
                    case 'PARTICIPANT':
                        setParticipantEditMode(true);
                        closeParticipantsAttendanceChannel();
                        getParticipantsAttendance(timeutil.getEpoch(participantDateRange[0]), timeutil.getEpoch(participantDateRange[1]));
                        getParticipantsEvents(timeutil.getEpoch(participantDateRange[0]), timeutil.getEpoch(participantDateRange[1]));
                        reRenderEntity("PARTICIPANT");
                        break;
                }
                dispatch(resetOperationType(null));
                break;
        }

    }, [attendanceReducer.operationType])


    useEffect(() => {
        if (instructorBulkModeOn && instructorBulkActionObj) {

            const entityAttendanceSettings = getEntityAttedanceSettings('INSTRUCTOR');
            instructorsBulkActionList.forEach((record) => {
                const [date, slot, center, entity, batch, Package, startTime, endTime, bookingId] = record.split('-$-');
                const dateSlotCenterEntityBatchPackageKey = `${date}-$-${slot}-$-${center}-$-${entity}-$-${batch}-$-${Package}`;

                const attendanceInDB = instructorsAttendanceMap.get(transformKey(dateSlotCenterEntityBatchPackageKey)) || null;
                if (!attendanceInDB && instructorOperationType === 'CLEAR') {
                    tempInstructorsAttendanceMapRef.current.delete(dateSlotCenterEntityBatchPackageKey);
                }
                else {
                    let newAttendanceState = { ...instructorBulkActionObj };
                    if (entityAttendanceSettings?.enableLateCheckInTracking && typeof (newAttendanceState.latestCheckIn) === 'number') {
                        newAttendanceState = Helper.checkIfCheckInLate(
                            newAttendanceState,
                            Number(startTime),
                            entityAttendanceSettings
                        );
                    }
                    if (entityAttendanceSettings?.enableLateCheckOutTracking && typeof (newAttendanceState.latestCheckOut) === 'number') {
                        newAttendanceState = Helper.checkIfCheckOutLate(
                            newAttendanceState,
                            Number(endTime),
                            entityAttendanceSettings
                        );
                    }
                    newAttendanceState = Helper.populateAttendanceDbKeys(
                        newAttendanceState,
                        attendanceInDB,
                        entity
                    );

                    tempInstructorsAttendanceMapRef.current.set(
                        dateSlotCenterEntityBatchPackageKey,
                        newAttendanceState
                    );
                }
            });
            resetEntityOperationType('INSTRUCTOR');
            reRenderEntity('INSTRUCTOR');
        }
    }, [instructorBulkActionObj]);

    useEffect(() => {

        if (participantBulkModeOn && participantBulkActionObj) {
            const entityAttendanceSettings = getEntityAttedanceSettings('PARTICIPANT');
            participantsBulkActionList.forEach((record) => {
                const [date, slot, center, entity, batch, Package, startTime, endTime, bookingId] = record.split('-$-');
                const dateSlotCenterEntityBatchPackageKey = `${date}-$-${slot}-$-${center}-$-${entity}-$-${batch}-$-${Package}`;

                const attendanceInDB = participantsAttendanceMap.get(transformKey(dateSlotCenterEntityBatchPackageKey)) || null;

                if (!attendanceInDB && participantOperationType === 'CLEAR') {
                    tempParticipantsAttendanceMapRef.current.delete(dateSlotCenterEntityBatchPackageKey);
                }
                else {
                    let newAttendanceState = { ...participantBulkActionObj };
                    if (entityAttendanceSettings?.enableLateCheckInTracking && typeof (newAttendanceState.latestCheckIn) === 'number') {
                        newAttendanceState = Helper.checkIfCheckInLate(
                            newAttendanceState,
                            Number(startTime),
                            entityAttendanceSettings
                        );
                    }
                    if (entityAttendanceSettings?.enableLateCheckOutTracking && typeof (newAttendanceState.latestCheckOut) === 'number') {
                        newAttendanceState = Helper.checkIfCheckOutLate(
                            newAttendanceState,
                            Number(endTime),
                            entityAttendanceSettings
                        );
                    }
                    newAttendanceState = Helper.populateAttendanceDbKeys(
                        newAttendanceState,
                        attendanceInDB,
                        entity
                    );

                    tempParticipantsAttendanceMapRef.current.set(dateSlotCenterEntityBatchPackageKey, newAttendanceState);
                }
            });
            resetEntityOperationType('PARTICIPANT');
            reRenderEntity('PARTICIPANT');
        }
    }, [participantBulkActionObj]);


    function closeAttendanceChannels() {
        closeInstructorsAttendanceChannel();
        closeParticipantsAttendanceChannel();
    }

    const closeParticipantsAttendanceChannel = () => {
        if (participantsAttendanceChannel) {
            participantsAttendanceChannel.close();
        }
    }

    const closeInstructorsAttendanceChannel = () => {
        if (instructorsAttendanceChannel) {
            instructorsAttendanceChannel.close();
        }
    }

    function getParticipantsAttendance(startDate, endDate) {
        dispatch(getParticipantsDateRangeAttendance({
            firebase,
            startDate,
            endDate
        }));
    }

    function getInstructorsAttendance(startDate, endDate) {
        dispatch(getInstructorsDateRangeAttendance({
            firebase,
            startDate,
            endDate
        }));
    }

    function getInstructorsEvents(startDate, endDate) {
        dispatch(getAllEventsOfInstructors({
            firebase,
            startDate,
            endDate,
            // status: "ACTIVE"
        }));
    }

    function getParticipantsEvents(startDate, endDate) {
        dispatch(getAllEventsOfParticipants({
            firebase,
            startDate,
            endDate,
            // status: "ACTIVE"
        }));
    }

    function getCheckInCheckOutTime(attendance) {

        const checkInTs = attendance.latestCheckIn;
        const checkOutTs = attendance.latestCheckOut;

        if (!checkInTs && !checkOutTs) return null;

        const checkInTime = checkInTs
            ? timeutil.getInstance(attendance.latestCheckIn).format("hh:mm A")
            : formatMsg('small.pending');
        const checkOutTime = checkOutTs
            ? timeutil.getInstance(attendance.latestCheckOut).format("hh:mm A")
            : formatMsg('small.pending');


        const checkInLabel = `${attendance.lateIn ? formatMsg('lateIn') : formatMsg('capital.in')} ${checkInTime}`;
        const checkOutLabel = `${attendance.lateOut ? formatMsg('lateOut') : formatMsg('out')} ${checkOutTime}`;

        return (
            <div className={styles.tableHeaderSubLabel}>
                <span className={styles.tableHeaderSubLabel} style={{ color: attendance.lateIn ? "#E56C32" : checkInTime === formatMsg('small.pending') ? "#FC7F03" : "#00855A" }}>
                    {checkInLabel}
                </span>
                <span className={styles.tableHeaderSubLabel} style={{ color: attendance.lateOut ? "#E56C32" : checkOutTime === formatMsg('small.pending') ? "#FC7F03" : "#00855A" }}>
                    {" - " + checkOutLabel}
                </span>
            </div>
        )
    }

    function getEntityAttedanceSettings(entityType) {

        let entityAttendanceSettings = null;

        switch (entityType) {
            case 'INSTRUCTOR':
                entityAttendanceSettings = instructorAttendanceSettings;
                break;
            case 'PARTICIPANT':
                entityAttendanceSettings = participantAttendanceSettings;
                break;
        }

        return entityAttendanceSettings;
    }

    function getEntityStateAttendanceMap(entityType) {

        let stateAttendanceMap = null;

        switch (entityType) {
            case 'INSTRUCTOR':
                stateAttendanceMap = tempInstructorsAttendanceMapRef.current;
                break;
            case 'PARTICIPANT':
                stateAttendanceMap = tempParticipantsAttendanceMapRef.current;
                break;
        }

        return stateAttendanceMap;
    }

    function getEntityDBAttendanceMap(entityType) {

        let dbAttendanceMap = null;

        switch (entityType) {
            case 'INSTRUCTOR':
                dbAttendanceMap = instructorsAttendanceMap;
                break;
            case 'PARTICIPANT':
                dbAttendanceMap = participantsAttendanceMap;
                break;
        }

        return dbAttendanceMap;
    }
    function transformKey(dateSlotCenterEntityBatchPackageKey) {

        const [date, slot, center, entity, batch, Package] = dateSlotCenterEntityBatchPackageKey.split('-$-');
        return `${date}--${slot}--${center}--${entity}`;
    }
    function getEntityDisplayAttendanceObj(entityType, dateSlotCenterEntityBatchPackageKey) {

        const stateAttendanceMap = getEntityStateAttendanceMap(entityType);
        const dbAttendanceMap = getEntityDBAttendanceMap(entityType);

        const attendanceInState = stateAttendanceMap.get(dateSlotCenterEntityBatchPackageKey) || null;
        const attendanceInDB = dbAttendanceMap.get(transformKey(dateSlotCenterEntityBatchPackageKey)) || null;

        const displayAttendanceObj = (attendanceInState || attendanceInDB)
            ? {
                ...(attendanceInDB || {}),
                ...(attendanceInState || {})
            }
            : null;

        return displayAttendanceObj;
    }

    function getIsEntityTimePickerVisible(
        entityType,
        validValue: string
    ): boolean {

        let stateValue = '';

        switch (entityType) {
            case 'INSTRUCTOR':
                stateValue = showInstructorTimePicker;
                break;
            case 'PARTICIPANT':
                stateValue = showParticipantTimePicker;
                break;
        }

        const isTimePickerVisible = stateValue === validValue;

        return isTimePickerVisible;
    }

    function getIsEntityBulkModeOn(entityType) {

        let bulkModeOn = false;

        switch (entityType) {
            case 'INSTRUCTOR':
                bulkModeOn = instructorBulkModeOn;
                break;
            case 'PARTICIPANT':
                bulkModeOn = participantBulkModeOn;
                break;
        }

        return bulkModeOn;
    }

    function getEntityOperationType(
        entityType: AttendanceEntityType
    ): AttendanceOperationType | undefined {

        let operationType: AttendanceOperationType | undefined = undefined;

        switch (entityType) {
            case 'INSTRUCTOR':
                operationType = instructorOperationType;
                break;
            case 'PARTICIPANT':
                operationType = participantOperationType;
                break;
        }

        return operationType;
    }

    function getIndividualCheckInCheckOutValue(dateSlotCenterEntityBatchPackageKey, entityType: AttendanceEntityType, entityId: string) {

        let returnEpoch = null;

        let operationType: AttendanceOperationType | undefined = getEntityOperationType(entityType);
        const displayAttendanceObj = getEntityDisplayAttendanceObj(entityType, dateSlotCenterEntityBatchPackageKey);

        switch (operationType) {
            case 'CHECKIN':
                returnEpoch = displayAttendanceObj?.latestCheckIn || null;
                break;
            case 'CHECKOUT':
                returnEpoch = displayAttendanceObj?.latestCheckOut || null;
                break;
        }

        return returnEpoch ? timeutil.getInstance(returnEpoch) : null;
    }

    function hideTimePicker(entityType: AttendanceEntityType) {

        resetShowEntityTimePicker(entityType);
        resetEntityOperationType(entityType);
    }

    function getEntityOperationTypeSetter(
        entityType: AttendanceEntityType
    ) {

        let setEntityOperationType: any = undefined;

        switch (entityType) {
            case 'INSTRUCTOR':
                setEntityOperationType = setInstructorOperationType;
                break;
            case 'PARTICIPANT':
                setEntityOperationType = setParticipantOperationType;
                break;
        }

        return setEntityOperationType;
    }

    function getShowEntityTimePickerSetter(
        entityType: AttendanceEntityType
    ) {

        let setShowEntityTimePicker: any = undefined;

        switch (entityType) {
            case 'INSTRUCTOR':
                setShowEntityTimePicker = setShowInstructorTimePicker;
                break;
            case 'PARTICIPANT':
                setShowEntityTimePicker = setShowParticipantTimePicker;
                break;
        }

        return setShowEntityTimePicker;
    }

    function getEntityBulkActionObj(entityType: AttendanceEntityType) {

        let entityBulkActionObj = undefined;

        switch (entityType) {
            case 'INSTRUCTOR':
                entityBulkActionObj = instructorBulkActionObj;
                break;
            case 'PARTICIPANT':
                entityBulkActionObj = participantBulkActionObj;
                break;
        }

        return entityBulkActionObj;
    }

    function getEntityBulkActionObjSetter(entityType: AttendanceEntityType) {

        let entityBulkActionObjSetter = undefined;

        switch (entityType) {
            case 'INSTRUCTOR':
                entityBulkActionObjSetter = setInstructorBulkActionObj;
                break;
            case 'PARTICIPANT':
                entityBulkActionObjSetter = setParticipantBulkActionObj;
                break;
        }

        return entityBulkActionObjSetter;
    }

    function resetShowEntityTimePicker(entityType: AttendanceEntityType) {

        const setShowEntityTimePicker = getShowEntityTimePickerSetter(entityType);
        setShowEntityTimePicker(undefined);
    }

    function resetEntityOperationType(entityType: AttendanceEntityType) {

        const setEntityOperationType = getEntityOperationTypeSetter(entityType);
        setEntityOperationType(undefined);
    }

    function reRenderEntity(entityType: AttendanceEntityType): void {

        switch (entityType) {
            case 'INSTRUCTOR':
                setReRenderInstructor(prev => !prev);
                break;
            case 'PARTICIPANT':
                setReRenderParticipant(prev => !prev);
                break;
        }
    }

    function onIndividualTimeChange(record, dateSlotCenterEntityBatchPackageKey, timeObj, entityType: AttendanceEntityType, entityId: string) {

        const inputEpoch = timeObj?.valueOf() || null;

        const operationType = getEntityOperationType(entityType);

        const stateAttendanceMap = getEntityStateAttendanceMap(entityType);
        const dbAttendanceMap = getEntityDBAttendanceMap(entityType);

        const attendanceInState = stateAttendanceMap.get(dateSlotCenterEntityBatchPackageKey) || {};
        const attendanceInDB = dbAttendanceMap.get(transformKey(dateSlotCenterEntityBatchPackageKey)) || null;

        const entityAttendanceSettings = getEntityAttedanceSettings(entityType);
        let newAttendanceState = null;

        switch (operationType) {
            case 'CHECKIN':
                newAttendanceState = Helper.buildAttendanceObj(
                    'CHECKIN',
                    attendanceInState,
                    { latestCheckIn: inputEpoch }
                );
                if (entityAttendanceSettings?.enableLateCheckInTracking && typeof (newAttendanceState.latestCheckIn) === 'number') {

                    newAttendanceState = Helper.checkIfCheckInLate(
                        newAttendanceState,
                        record?.startTime,
                        entityAttendanceSettings
                    );
                }

                newAttendanceState = Helper.populateAttendanceDbKeys(
                    newAttendanceState,
                    attendanceInDB,
                    entityId
                );
                stateAttendanceMap.set(dateSlotCenterEntityBatchPackageKey, newAttendanceState);
                break;
            case 'CHECKOUT':
                newAttendanceState = Helper.buildAttendanceObj(
                    'CHECKOUT',
                    attendanceInState,
                    { latestCheckOut: inputEpoch }
                );
                if (entityAttendanceSettings?.enableLateCheckOutTracking && typeof (newAttendanceState.latestCheckOut) === 'number') {
                    newAttendanceState = Helper.checkIfCheckOutLate(
                        newAttendanceState,
                        record?.endTime,
                        entityAttendanceSettings
                    );
                }
                newAttendanceState = Helper.populateAttendanceDbKeys(
                    newAttendanceState,
                    attendanceInDB,
                    entityId
                );
                stateAttendanceMap.set(dateSlotCenterEntityBatchPackageKey, newAttendanceState);
                break;
        }

        // Clean up when db and state all values are null 
        if (
            !newAttendanceState.latestCheckIn && !newAttendanceState.latestCheckOut &&
            !attendanceInDB?.latestCheckIn && !attendanceInDB?.latestCheckOut
        ) {
            stateAttendanceMap.delete(dateSlotCenterEntityBatchPackageKey);
        }

        // Hide time picker only on time selection, not on input clear 
        if (inputEpoch) {
            hideTimePicker(entityType);
            onSubmit(entityType);
        }

        reRenderEntity(entityType);
    }


    function onIndividualPress(
        dateSlotCenterEntityBatchPackageKey,
        operationType: AttendanceOperationType,
        entityType: AttendanceEntityType,
        entityId: string
    ) {

        const setEntityOperationType = getEntityOperationTypeSetter(entityType);
        const setShowEntityTimePicker = getShowEntityTimePickerSetter(entityType);

        const stateAttendanceMap = getEntityStateAttendanceMap(entityType);
        const dbAttendanceMap = getEntityDBAttendanceMap(entityType);

        const attendanceInState = stateAttendanceMap.get(dateSlotCenterEntityBatchPackageKey) || {};
        const attendanceInDB = dbAttendanceMap.get(transformKey(dateSlotCenterEntityBatchPackageKey)) || null;

        let newAttendanceState = null;

        switch (operationType) {
            case 'CHECKIN':
            case 'CHECKOUT':
                setShowEntityTimePicker(dateSlotCenterEntityBatchPackageKey);
                break;
            case 'PRESENT':
                newAttendanceState = Helper.buildAttendanceObj(operationType, attendanceInState);
                newAttendanceState = Helper.populateAttendanceDbKeys(
                    newAttendanceState,
                    attendanceInDB,
                    entityId
                );
                stateAttendanceMap.set(dateSlotCenterEntityBatchPackageKey, newAttendanceState);
                reRenderEntity(entityType);
                onSubmit(entityType);
                break;
            case 'ABSENT':
                newAttendanceState = Helper.buildAttendanceObj(operationType, attendanceInState);
                newAttendanceState = Helper.populateAttendanceDbKeys(
                    newAttendanceState,
                    attendanceInDB,
                    entityId
                );
                stateAttendanceMap.set(dateSlotCenterEntityBatchPackageKey, newAttendanceState);
                reRenderEntity(entityType);
                openAbsentModal(entityType, entityId, dateSlotCenterEntityBatchPackageKey);

                break;
            case 'CLEAR':
                if (attendanceInDB) {
                    newAttendanceState = Helper.buildAttendanceObj('CLEAR', attendanceInState);
                    newAttendanceState = Helper.populateAttendanceDbKeys(
                        newAttendanceState,
                        attendanceInDB,
                        entityId
                    );
                    stateAttendanceMap.set(dateSlotCenterEntityBatchPackageKey, newAttendanceState);
                }
                else {
                    stateAttendanceMap.delete(dateSlotCenterEntityBatchPackageKey);
                }
                reRenderEntity(entityType);
                onSubmit(entityType);
                break;
        }

        setEntityOperationType(operationType);
    }
    function getIndividualReferenceTime(entityType: AttendanceEntityType, entityId: string, dateSlotCenterEntityBatchPackageKey: string) {
        const displayAttendanceObj = getEntityDisplayAttendanceObj(entityType, dateSlotCenterEntityBatchPackageKey);

        const checkIn = displayAttendanceObj?.latestCheckIn;
        const checkOut = displayAttendanceObj?.latestCheckOut;

        if (getEntityOperationType(entityType) === 'CHECKOUT' && checkIn) {
            return timeutil.getInstance(checkIn);
        }

        if (getEntityOperationType(entityType) === 'CHECKIN' && checkOut) {
            return timeutil.getInstance(checkOut);
        }

        return null;
    }


    function getDisabledTime(
        entityType: AttendanceEntityType,
        getRefTimeFn: (entityType: AttendanceEntityType) => moment.Moment | null
    ) {
        const refTime = getRefTimeFn(entityType);
        if (!refTime) return { disabledHours: () => [], disabledMinutes: () => [] };

        const refHour = refTime.hour();
        const refMinute = refTime.minute();
        const isCheckOut = getEntityOperationType(entityType) === 'CHECKOUT';

        return {
            disabledHours: () => {
                const hours: number[] = [];
                for (let i = 0; i < 24; i++) {
                    if ((isCheckOut && i < refHour) || (!isCheckOut && i > refHour)) {
                        hours.push(i);
                    }
                }
                return hours;
            },
            disabledMinutes: (selectedHour: number) => {
                if (selectedHour !== refHour) return [];

                const minutes: number[] = [];
                for (let i = 0; i < 60; i++) {
                    if ((isCheckOut && i < refMinute) || (!isCheckOut && i > refMinute)) {
                        minutes.push(i);
                    }
                }
                return minutes;
            }
        };
    }

    function renderCheckInCheckOutAction(entityType, entityId, dateSlotCenterEntityBatchPackageKey, attendanceStatus, canMarkAttendance) {
        return (
            <div className={styles.entityContainer}>
                <div
                    className={`${styles.imgContainer} ${!canMarkAttendance ? styles.disabled : ""}`}
                    onClick={!canMarkAttendance ? undefined : () => onIndividualPress(dateSlotCenterEntityBatchPackageKey, 'CHECKIN', entityType, entityId)}
                >
                    <Tooltip
                        title={formatMsg("checkIn")}
                        rootClassName='purple-tooltip'
                        placement="bottom"
                    >
                        <img
                            className={styles.actionImg}
                            src='/images/checkin.png'
                        />
                    </Tooltip>
                </div>
                <div
                    className={`${styles.imgContainer} ${!canMarkAttendance ? styles.disabled : ""}`}
                    onClick={!canMarkAttendance ? undefined : () => onIndividualPress(dateSlotCenterEntityBatchPackageKey, 'CHECKOUT', entityType, entityId)}
                >
                    <Tooltip
                        title={formatMsg("checkOut")}
                        rootClassName='purple-tooltip'
                        placement="bottom"
                    >
                        <img
                            className={styles.actionImg}
                            src='/images/checkout.png'
                        />
                    </Tooltip>

                </div>
                {attendanceStatus === 'ABSENT' ? null : (
                    <div
                        className={`${styles.imgContainer} ${!canMarkAttendance ? styles.disabled : ""}`}
                        onClick={!canMarkAttendance ? undefined : () => onIndividualPress(dateSlotCenterEntityBatchPackageKey, 'ABSENT', entityType, entityId)}
                    >
                        <Tooltip
                            title={formatMsg("markAbsent")}
                            rootClassName='purple-tooltip pink-tooltip'
                            placement="bottom"
                        >
                            <img
                                className={styles.actionImg}
                                src='/images/absent.png'
                            />
                        </Tooltip>
                    </div>
                )}
                {(attendanceStatus === 'PRESENT' || attendanceStatus === 'ABSENT') && (
                    <div
                        className={`${styles.imgContainer} ${!canMarkAttendance ? styles.disabled : ""}`}
                        onClick={!canMarkAttendance ? undefined : () => onIndividualPress(dateSlotCenterEntityBatchPackageKey, 'CLEAR', entityType, entityId)}
                    >
                        <Tooltip
                            title={formatMsg("resetAttendance")}
                            rootClassName='purple-tooltip'
                            placement="bottom"
                        >
                            <img
                                className={styles.actionImg}
                                src='/images/clear.png'
                            />
                        </Tooltip>
                    </div>
                )}
            </div>
        )
    }

    function renderPresentAbsentAction(entityType, entityId, dateSlotCenterEntityBatchPackageKey, attendanceStatus, canMarkAttendance) {
        return (
            <div className={styles.entityContainer}>
                {attendanceStatus === 'PRESENT' ? null : (
                    <div
                        className={`${styles.imgContainer} ${!canMarkAttendance ? styles.disabled : ""}`}
                        onClick={!canMarkAttendance ? undefined : () => onIndividualPress(dateSlotCenterEntityBatchPackageKey, 'PRESENT', entityType, entityId)}
                    >
                        <Tooltip
                            title={formatMsg("markPresent")}
                            rootClassName='purple-tooltip green-tooltip'
                            placement="bottom"
                        >
                            <img
                                className={styles.actionImg}
                                src='/images/present.png'
                            />
                        </Tooltip>
                    </div>
                )}
                {attendanceStatus === 'ABSENT' ? null : (
                    <div
                        className={`${styles.imgContainer} ${!canMarkAttendance ? styles.disabled : ""}`}
                        onClick={!canMarkAttendance ? undefined : () => onIndividualPress(dateSlotCenterEntityBatchPackageKey, 'ABSENT', entityType, entityId)}
                    >
                        <Tooltip
                            title={formatMsg("markAbsent")}
                            rootClassName='purple-tooltip pink-tooltip'
                            placement="bottom"
                        >
                            <img
                                className={styles.actionImg}
                                src='/images/absent.png'
                            />
                        </Tooltip>
                    </div>
                )}
                {(attendanceStatus === 'PRESENT' || attendanceStatus === 'ABSENT') && (
                    <div
                        className={`${styles.imgContainer} ${!canMarkAttendance ? styles.disabled : ""}`}
                        onClick={!canMarkAttendance ? undefined : () => onIndividualPress(dateSlotCenterEntityBatchPackageKey, 'CLEAR', entityType, entityId)}
                    >
                        <Tooltip
                            title={formatMsg("resetAttendance")}
                            rootClassName='purple-tooltip'
                            placement="bottom"
                        >
                            <img
                                className={styles.actionImg}
                                src='/images/clear.png'
                            />
                        </Tooltip>
                    </div>
                )}
            </div>
        )
    }

    function renderIndividualAttendanceActions(entityType, entityId, record, canMarkAttendance) {
        const dateSlotCenterEntityBatchPackageKey = `${timeutil.inFormat(record.date, 'DD-MM-YY')}-$-${record.slotRandomId}-$-${record.centerId}-$-${entityId}-$-${record.batchId}-$-${record.packageId}`;
        // const dateSlotCenterEntityBatchPackageKey = `${timeutil.inFormat(record.date, 'DD-MM-YY')}--${record.slotRandomId}--${record.centerId}--${entityId}`;
        const displayAttendanceObj = getEntityDisplayAttendanceObj(entityType, dateSlotCenterEntityBatchPackageKey);
        const attendanceStatus = Helper.getAttendanceStatus(displayAttendanceObj);
        const isTimePickerVisible: boolean = getIsEntityTimePickerVisible(entityType, dateSlotCenterEntityBatchPackageKey);
        const entityAttendanceSettings = getEntityAttedanceSettings(entityType);
        const isEntityBulkModeOn = getIsEntityBulkModeOn(entityType);

        // If permission check is provided and user doesn't have permission, don't render individual actions
        if (canMarkAttendance === false) {
            return null;
        }

        if (isEntityBulkModeOn) {
            return null;
        }

        return (
            <div className={styles.attendanceActions}>
                {entityAttendanceSettings?.type === 'checkin_checkout' ? (
                    isTimePickerVisible ? (
                        <div className={styles.timePickerContainer}>
                            <TimePicker
                                open={isTimePickerVisible}
                                onOpenChange={() => hideTimePicker(entityType)}
                                value={getIndividualCheckInCheckOutValue(dateSlotCenterEntityBatchPackageKey, entityType, entityId)}
                                onChange={(epoch, dateStr) => {
                                    onIndividualTimeChange(record, dateSlotCenterEntityBatchPackageKey, epoch, entityType, entityId);
                                }}
                                defaultOpenValue={timeutil.getInstance('00:00', 'hh:mm a')}
                                format='hh:mm a'
                                disabledTime={() => getDisabledTime(entityType, (et) => getIndividualReferenceTime(et, entityId, dateSlotCenterEntityBatchPackageKey))}
                            />
                            <div
                                className={styles.imgContainer}
                                onClick={() => hideTimePicker(entityType)}
                            >
                                <img
                                    className={styles.tickImg}
                                    src='/images/cancelGray.png'
                                />
                            </div>
                        </div>
                    ) : (
                        canMarkAttendance
                            ? renderCheckInCheckOutAction(
                                entityType,
                                entityId,
                                dateSlotCenterEntityBatchPackageKey,
                                attendanceStatus,
                                canMarkAttendance
                            )
                            : <Tooltip title={formatMsg("youDon'tHavePermissionToMarkAttendance")}>
                                {
                                    renderCheckInCheckOutAction(
                                        entityType,
                                        entityId,
                                        dateSlotCenterEntityBatchPackageKey,
                                        attendanceStatus,
                                        canMarkAttendance
                                    )
                                }
                            </Tooltip>
                    )
                ) : null}
                {entityAttendanceSettings?.type !== 'checkin_checkout' && (
                    canMarkAttendance
                        ? renderPresentAbsentAction(
                            entityType,
                            entityId,
                            dateSlotCenterEntityBatchPackageKey,
                            attendanceStatus,
                            canMarkAttendance
                        )
                        : <Tooltip title={formatMsg("youDon'tHavePermissionToMarkAttendance")}>
                            {
                                renderPresentAbsentAction(
                                    entityType,
                                    entityId,
                                    dateSlotCenterEntityBatchPackageKey,
                                    attendanceStatus,
                                    canMarkAttendance
                                )
                            }
                        </Tooltip>
                )}
            </div>
        );
    }

    function getBulkReferenceTime(entityType: AttendanceEntityType) {
        const entityBulkActionObj: any = getEntityBulkActionObj(entityType);
        const operationType: AttendanceOperationType | undefined = getEntityOperationType(entityType);

        const checkIn = entityBulkActionObj?.latestCheckIn;
        const checkOut = entityBulkActionObj?.latestCheckOut;

        if (operationType === 'CHECKOUT' && checkIn) {
            return timeutil.getInstance(checkIn);
        }

        if (operationType === 'CHECKIN' && checkOut) {
            return timeutil.getInstance(checkOut);
        }

        return null;
    }

    function renderBulkAttendanceActions(entityType: AttendanceEntityType, bulkType: AttendanceBulkType, canMarkAttendance?: boolean) {
        const isEntityBulkModeOn = getIsEntityBulkModeOn(entityType);
        const isTimePickerVisible = getIsEntityTimePickerVisible(entityType, bulkType);

        const entityAttendanceSettings = getEntityAttedanceSettings(entityType);
        const setShowEntityTimePicker = getShowEntityTimePickerSetter(entityType);

        // If permission check is provided and user doesn't have permission, don't render bulk actions
        if (canMarkAttendance === false) {
            return null;
        }

        return isEntityBulkModeOn
            ? (
                <div className={styles.attendanceActions}>
                    {
                        entityAttendanceSettings?.type === 'checkin_checkout'
                            ? isTimePickerVisible
                                ? <div className={styles.timePickerContainer}>
                                    <TimePicker
                                        open={isTimePickerVisible}
                                        onOpenChange={() => hideTimePicker(entityType)}
                                        value={getBulkCheckInCheckOutValue(entityType)}
                                        onChange={(epoch, dateStr) => {
                                            onBulkTimeChange(epoch, entityType)
                                        }}
                                        defaultOpenValue={timeutil.getInstance('00:00', 'hh:mm a')}
                                        format='hh:mm a'
                                        disabledTime={() => getDisabledTime(entityType, getBulkReferenceTime)}
                                    />
                                    <div
                                        className={styles.imgContainer}
                                        onClick={() => hideTimePicker(entityType)}
                                    >
                                        <img
                                            src='/images/cancelGray.png'
                                            className={styles.tickImg}
                                        />
                                    </div>
                                </div>
                                : <div className={styles.entityContainer}>
                                    <div
                                        className={styles.imgContainer}
                                        onClick={() => onBulkPress('CHECKIN', entityType)}
                                    >
                                        <Tooltip
                                            title={formatMsg("checkIn")}
                                            rootClassName='purple-tooltip'
                                            placement="bottom"
                                        >
                                            <img
                                                className={styles.actionImg}
                                                src='/images/checkin.png'
                                            />
                                        </Tooltip>
                                    </div>
                                    <div
                                        className={styles.imgContainer}
                                        onClick={() => onBulkPress('CHECKOUT', entityType)}
                                    >
                                        <Tooltip
                                            title={formatMsg("checkOut")}
                                            rootClassName='purple-tooltip'
                                            placement="bottom"
                                        >
                                            <img
                                                className={styles.actionImg}
                                                src='/images/checkout.png'
                                            />
                                        </Tooltip>
                                    </div>
                                    <div
                                        className={styles.imgContainer}
                                        onClick={() => onBulkPress('ABSENT', entityType)}
                                    >
                                        <Tooltip
                                            title={formatMsg("markAbsent")}
                                            rootClassName='purple-tooltip pink-tooltip'
                                            placement="bottom"
                                        >
                                            <img
                                                className={styles.actionImg}
                                                src='/images/absent.png'
                                            />
                                        </Tooltip>
                                    </div>
                                    <div
                                        className={styles.imgContainer}
                                        onClick={() => onBulkPress('CLEAR', entityType)}
                                    >
                                        <Tooltip
                                            title={formatMsg("resetAttendance")}
                                            rootClassName='purple-tooltip'
                                            placement="bottom"
                                        >
                                            <img
                                                className={styles.actionImg}
                                                src='/images/clear.png'
                                            />
                                        </Tooltip>
                                    </div>
                                </div>
                            : null
                    }
                    {
                        entityAttendanceSettings?.type !== 'checkin_checkout' &&
                        <div className={styles.entityContainer}>
                            <div
                                className={styles.imgContainer}
                                onClick={() => onBulkPress('PRESENT', entityType)}
                            >
                                <Tooltip
                                    title={formatMsg("markPresent")}
                                    rootClassName='purple-tooltip green-tooltip'
                                    placement="bottom"
                                >
                                    <img
                                        className={styles.actionImg}
                                        src='/images/present.png'
                                    />
                                </Tooltip>
                            </div>
                            <div
                                className={styles.imgContainer}
                                onClick={() => onBulkPress('ABSENT', entityType)}
                            >
                                <Tooltip
                                    title={formatMsg("markAbsent")}
                                    rootClassName='purple-tooltip pink-tooltip'
                                    placement="bottom"
                                >
                                    <img
                                        className={styles.actionImg}
                                        src='/images/absent.png'
                                    />
                                </Tooltip>
                            </div>
                            <div
                                className={styles.imgContainer}
                                onClick={() => onBulkPress('CLEAR', entityType)}
                            >
                                <Tooltip
                                    title={formatMsg("resetAttendance")}
                                    rootClassName='purple-tooltip'
                                    placement="bottom"
                                >
                                    <img
                                        className={styles.actionImg}
                                        src='/images/clear.png'
                                    />
                                </Tooltip>
                            </div>
                        </div>
                    }
                </div>
            )
            : null
    }

    function onBulkPress(operationType: AttendanceOperationType, entityType: AttendanceEntityType) {

        const entityBulkActionObj = getEntityBulkActionObj(entityType);
        const setEntityBulkActionObj = getEntityBulkActionObjSetter(entityType);
        const setEntityOperationType = getEntityOperationTypeSetter(entityType);
        const setShowEntityTimePicker = getShowEntityTimePickerSetter(entityType);

        let newBulkActionObj = null;

        switch (operationType) {
            case 'CHECKIN':
            case 'CHECKOUT':
                setEntityOperationType(operationType);
                setShowEntityTimePicker(`BULK_${entityType}S`);
                break;

            case 'PRESENT':
                newBulkActionObj = Helper.buildAttendanceObj(operationType, entityBulkActionObj);
                setEntityBulkActionObj(newBulkActionObj);
                break;

            case 'ABSENT':
                newBulkActionObj = Helper.buildAttendanceObj(operationType, entityBulkActionObj);
                setEntityBulkActionObj(newBulkActionObj);

                openAbsentModal(entityType, `BULK_${entityType}S`);
                break;

            case 'CLEAR':
                setEntityOperationType(operationType);
                newBulkActionObj = Helper.buildAttendanceObj('CLEAR', entityBulkActionObj);
                setEntityBulkActionObj(newBulkActionObj);
                break;
        }
    }

    function onBulkTimeChange(timeObj, entityType: AttendanceEntityType) {

        const operationType: AttendanceOperationType | undefined = getEntityOperationType(entityType);
        const entityBulkActionObj = getEntityBulkActionObj(entityType);
        const setEntityBulkActionObj = getEntityBulkActionObjSetter(entityType);

        const entityAttendanceSettings = getEntityAttedanceSettings(entityType);
        const inputEpoch = timeObj?.valueOf();

        let newBulkActionObj = null;

        switch (operationType) {
            case 'CHECKIN':
                newBulkActionObj = Helper.buildAttendanceObj(
                    'CHECKIN',
                    entityBulkActionObj,
                    { latestCheckIn: inputEpoch || null }
                );
                setEntityBulkActionObj(newBulkActionObj);
                break;
            case 'CHECKOUT':
                newBulkActionObj = Helper.buildAttendanceObj(
                    'CHECKOUT',
                    entityBulkActionObj,
                    { latestCheckOut: inputEpoch || null }
                );
                setEntityBulkActionObj(newBulkActionObj);
                break;
        }

        // Hide time picker only on time selection, not on input clear 
        if (inputEpoch) hideTimePicker(entityType);
    }

    function getBulkCheckInCheckOutValue(entityType: AttendanceEntityType) {

        let returnEpoch = null;

        let operationType: AttendanceOperationType | undefined = getEntityOperationType(entityType);
        let entityBulkActionObj: any = getEntityBulkActionObj(entityType);

        switch (operationType) {
            case 'CHECKIN':
                returnEpoch = entityBulkActionObj?.latestCheckIn || null;
                break;
            case 'CHECKOUT':
                returnEpoch = entityBulkActionObj?.latestCheckOut || null;
                break;
        }

        return returnEpoch ? timeutil.getInstance(returnEpoch) : null;
    }

    function openAbsentModal(entityType: AttendanceEntityType, entityId: string, dateSlotCenterEntityBatchPackageKey = null) {
        const entityAttendanceSettings = getEntityAttedanceSettings(entityType);

        if (entityAttendanceSettings?.enableAbsentNotes) {
            setIsAbsentNoteModalOpen(true);
            setAbsentNoteEntityType(entityType);
            setAbsentNoteFor(entityId);
            setRecordKey(dateSlotCenterEntityBatchPackageKey)
        } else if (!participantBulkModeOn && !instructorBulkModeOn) {
            onSubmit(entityType);
        }
    }

    function handleAbsentNoteOk() {

        form.validateFields()
            .then((values) => {
                const absentNote = values.absentNote || null;

                const bulkTypes = ['BULK_INSTRUCTORS', 'BULK_PARTICIPANTS'];

                if (bulkTypes.includes(absentNoteFor)) {
                    const entityBulkActionObj = getEntityBulkActionObj(absentNoteEntityType);
                    const setEntityBulkActionObj = getEntityBulkActionObjSetter(absentNoteEntityType);

                    const newAttendanceState = {
                        ...entityBulkActionObj,
                        absentNote
                    };

                    setEntityBulkActionObj(newAttendanceState);
                }
                else {

                    const stateAttendanceMap = getEntityStateAttendanceMap(absentNoteEntityType);
                    const attendanceInState = stateAttendanceMap.get(recordKey) || {};

                    let newAttendanceState = {
                        ...attendanceInState,
                        absentNote
                    };

                    stateAttendanceMap.set(recordKey, newAttendanceState);
                    reRenderEntity(absentNoteEntityType);
                    onSubmit(absentNoteEntityType);
                }

                resetAbsentNoteStates();
            })
            .catch((error) => {
                notification("error", formatMsg("pleaseFillAllDetails"));
                console.error('Validation failed:', error);
            });
    }

    function handleAbsentNoteCancel() {
        resetAbsentNoteStates();
    }

    function resetAbsentNoteStates() {
        form.resetFields();
        setIsAbsentNoteModalOpen(false);
        setAbsentNoteEntityType(undefined);
        setAbsentNoteFor(undefined);
        setRecordKey(undefined);
    }

    // Records saved before the marker existed were all made by staff.
    function getMarkedByLabel(attendance) {
        if (!attendance) return null;
        const byParent = attendance.markedByType === 'PARENT';
        return (
            <div className={styles.markedBy} data-testid="attendance-marked-by">
                {formatMsg(byParent ? 'markedByParent' : 'markedByStaff')}
            </div>
        );
    }

    function getAbsentNote(entityType: AttendanceEntityType, dateSlotCenterEntityBatchPackageKey: string) {

        const displayAttendanceObj = getEntityDisplayAttendanceObj(entityType, dateSlotCenterEntityBatchPackageKey);

        if (displayAttendanceObj?.absentNote) {
            const note = displayAttendanceObj.absentNote;
            const isTruncated = note.length > 20;
            const displayedNote = isTruncated ? `${note.slice(0, 20)}...` : note;

            return <div className={styles.absentNote}>
                {isTruncated ? (
                    <Tooltip title={note}>
                        <span>{displayedNote}</span>
                    </Tooltip>
                ) : (
                    <span>{displayedNote}</span>
                )}
            </div>
        }

        return null;
    }

    function resetAllLocalStates(entityType) {
        if (entityType === "INSTRUCTOR") {
            tempInstructorsAttendanceMapRef.current = new Map();
            reRenderEntity('INSTRUCTOR');
            setShowInstructorTimePicker(undefined);
            setInstructorOperationType(undefined);
            setInstructorBulkActionList(undefined);
            setInstructorEditMode(false);
            setInstructorBulkActionObj(undefined);
            setInstructorBulkActionList(undefined);
        }
        else {
            tempParticipantsAttendanceMapRef.current = new Map();
            reRenderEntity('PARTICIPANT');
            setShowParticipantTimePicker(undefined);
            setParticipantOperationType(undefined);
            setParticipantBulkActionList(undefined);
            setParticipantEditMode(false);
            setParticipantBulkActionObj(undefined);
            setParticipantBulkActionList(undefined);
        }
    }

    function onCancel(entityType) {
        resetAllLocalStates(entityType);
    }

    function getEditModeSetter(
        entityType: AttendanceEntityType
    ) {

        let setEditMode: any = undefined;

        switch (entityType) {
            case 'INSTRUCTOR':
                setEditMode = setInstructorEditMode;
                break;
            case 'PARTICIPANT':
                setEditMode = setParticipantEditMode;
                break;
        }

        return setEditMode;
    }

    function onSubmit(entityType) {

        closeAttendanceChannels();
        const setEditMode = getEditModeSetter(entityType);
        setEditMode(false);

        dispatch(markMultipleEventsBulkAttendance({
            firebase,
            entityType,
            payload: {
                attendanceMap: getEntityStateAttendanceMap(entityType),
            }
        }));
    }

    const participantColumns = [
        {
            key: 'profileId',
            dataIndex: 'profileId',
            title: <div className={styles.tableHeader}>
                <div className={styles.tableHeaderLabel}>
                    {
                        (firebase?.schoolConfig?.useUserTypeAsDisplayName && firebase?.schoolConfig?.selectedUserTypeName)
                            ? firebase?.schoolConfig?.selectedUserTypeName
                            : formatMsg('users')
                    }
                </div>
                {isTeacherLoggedIn && <div className={styles.tableHeaderSubLabel}>
                    {formatMsg('account')}
                </div>}
            </div>,
            sorter: (a, b) => {
                const a1 = { ...a, participantName: participantsMap.get(a.profiledId)?.name || '' }
                const b1 = { ...b, participantName: participantsMap.get(b.profiledId)?.name || '' }
                return Helper.sortStringValue(a1, b1, "participantName")
            },
            render: (text, record) => {

                const profileObj = participantsMap.get(record.profileId) || {};
                const accountObj = accountsMap.get(record.accountId) || {};

                return (
                    <div className={styles.tableHeader}>
                        <div className={styles.tableHeaderLabel}>
                            {profileObj.name}
                        </div>
                        {isTeacherLoggedIn && <div className={styles.tableHeaderSubLabel}>
                            {accountObj.name}
                        </div>}
                    </div>
                );
            }
        },
        {
            key: 'packageId',
            dataIndex: 'packageId',
            title: <div className={styles.tableHeader}>
                <div className={styles.tableHeaderLabel}>
                    {formatMsg('package')}
                </div>
                <div className={styles.tableHeaderSubLabel}>
                    {formatMsg('batch')}
                </div>
            </div>,
            sorter: (a, b) => {
                const a1 = { ...a, packageName: packagesMap.get(a.packageId)?.name || '' }
                const b1 = { ...b, packageName: packagesMap.get(b.packageId)?.name || '' }
                return Helper.sortStringValue(a1, b1, "packageName")
            },
            render: (text, record) => {
                const packageObj = packagesMap.get(record.packageId) || {};

                return (
                    <div className={styles.tableHeader}>
                        <div className={styles.tableHeaderLabel}>
                            {packageObj.name}
                        </div>
                        <div className={styles.tableHeaderSubLabel}>
                            {record.batchName}
                        </div>
                    </div>
                );
            }
        },
        {
            key: 'centerId',
            dataIndex: 'centerId',
            title: <div className={styles.tableHeader}>
                <div className={styles.tableHeaderLabel}>
                    {formatMsg('center')}
                </div>
            </div>,
            sorter: (a, b) => {
                const a1 = { ...a, centerName: centersMap.get(a.centerId)?.name || '' }
                const b1 = { ...b, centerName: centersMap.get(b.centerId)?.name || '' }
                return Helper.sortStringValue(a1, b1, "centerName")
            },
            render: (text, record) => {

                const centerObj = centersMap.get(record.centerId) || {};

                return (
                    <div className={styles.tableHeader}>
                        <div className={styles.tableHeaderLabel}>
                            {centerObj.name}
                        </div>
                    </div>
                );
            }
        },
        {
            key: 'date',
            dataIndex: 'date',
            title: <div className={styles.tableHeader}>
                <div className={styles.tableHeaderLabel}>
                    {formatMsg('date')}
                </div>
                <div className={styles.tableHeaderSubLabel}>
                    {formatMsg('timeSlot')}
                </div>
            </div>,
            sorter: (a, b) => Helper.sortIntegerValue(a, b, "date"),
            render: (text, record) => {

                return (
                    <div className={styles.tableHeader}>
                        <div className={styles.tableHeaderLabel}>
                            {timeutil.getInstance(record.date).format(Helper.dateFormat(firebase))}
                        </div>
                        <div className={styles.tableHeaderSubLabel}>
                            {timeutil.getInstance(record.startTime).format("hh:mm A")} - {timeutil.getInstance(record.endTime).format("hh:mm A")}
                        </div>
                    </div>
                );
            }
        },
        {
            key: 'attendance',
            dataIndex: 'attendance',
            title: <div className={styles.attendanceHeader}>
                <div className={styles.tableHeaderLabel}>
                    {formatMsg('attendance')}
                </div>
            </div>,
            render: (_, record) => {
                const dateSlotCenterEntityBatchPackageKey = `${timeutil.inFormat(record.date, 'DD-MM-YY')}-$-${record.slotRandomId}-$-${record.centerId}-$-${record.profileId}-$-${record.batchId}-$-${record.packageId}`;
                const displayAttendanceObj = getEntityDisplayAttendanceObj('PARTICIPANT', dateSlotCenterEntityBatchPackageKey);
                const attendanceStatus: AttendanceStatus = Helper.getAttendanceStatus(displayAttendanceObj);
                return (
                    <div className={styles.attendanceHeader}>
                        {(attendanceStatus === 'CLEARED' || attendanceStatus === 'PENDING') && (
                            <div className={styles.attendanceHeader}>
                                <img
                                    className={styles.actionImg}
                                    src='/images/yellowExclaimation.png'
                                />
                            </div>
                        )}
                        {attendanceStatus === "ABSENT" && (
                            <div className={styles.attendanceHeader}>
                                <img
                                    className={styles.actionImg}
                                    src='/images/absent.png'
                                />
                                {getAbsentNote('PARTICIPANT', dateSlotCenterEntityBatchPackageKey)}
                                {getMarkedByLabel(displayAttendanceObj)}
                            </div>
                        )}
                        {attendanceStatus === "PRESENT" && (
                            <div className={styles.attendanceHeader}>
                                <img
                                    className={styles.actionImg}
                                    src='/images/present.png'
                                />
                                {
                                    getCheckInCheckOutTime(displayAttendanceObj)
                                }
                                {showSlotTimer && (
                                    <SlotTimerPill
                                        record={record}
                                        attendance={displayAttendanceObj}
                                        status={attendanceStatus}
                                        settings={participantAttendanceSettings}
                                    />
                                )}
                                {getMarkedByLabel(displayAttendanceObj)}
                            </div>
                        )}
                    </div>
                );
            },
        },
        ...(isTeacherLoggedIn
            ? [
                {
                    key: 'actions',
                    title: (
                        <div className={styles.entityContainer}>
                            {renderBulkAttendanceActions('PARTICIPANT', 'BULK_PARTICIPANTS',
                                allParticipantsEvents?.some(record =>
                                    permissions.hasPermissionForEntity(firebase, permissions.PERMISSIONS.ATTENDANCE_MARK, [record.centerId])
                                )
                            )}
                        </div>
                    ),
                    render: (_, record) => {
                        const canMarkAttendance = permissions.hasPermissionForEntity(firebase, permissions.PERMISSIONS.ATTENDANCE_MARK, [record.centerId])
                        return (
                            <div className={styles.entityContainer}>
                                {renderIndividualAttendanceActions('PARTICIPANT', record.profileId, record, canMarkAttendance)}
                            </div>
                        )
                    },
                },
            ]
            : []),
    ];

    const instructorColumns = [
        {
            key: 'staffId',
            dataIndex: 'staffId',
            title: <div className={styles.tableHeader}>
                <div className={styles.tableHeaderLabel}>
                    {formatMsg('staff')}
                </div>
            </div>,
            sorter: (a, b) => {
                const a1 = { ...a, staffName: teachersMap.get(a.staffId)?.name || '' }
                const b1 = { ...b, staffName: teachersMap.get(b.staffId)?.name || '' }
                return Helper.sortStringValue(a1, b1, "staffName")
            },
            render: (text, record) => {

                const staffObj = teachersMap.get(record.staffId) || {};

                return (
                    <div className={styles.tableHeader}>
                        <div className={styles.tableHeaderLabel}>
                            {staffObj.name}
                        </div>
                    </div>
                );
            }
        },
        {
            key: 'packageId',
            dataIndex: 'packageId',
            title: <div className={styles.tableHeader}>
                <div className={styles.tableHeaderLabel}>
                    {formatMsg('package')}
                </div>
                <div className={styles.tableHeaderSubLabel}>
                    {formatMsg('batch')}
                </div>
            </div>,
            sorter: (a, b) => {
                const a1 = { ...a, packageName: packagesMap.get(a.packageId)?.name || '' }
                const b1 = { ...b, packageName: packagesMap.get(b.packageId)?.name || '' }
                return Helper.sortStringValue(a1, b1, "packageName")
            },
            render: (text, record) => {

                const packageObj = packagesMap.get(record.packageId) || {};

                return (
                    <div className={styles.tableHeader}>
                        <div className={styles.tableHeaderLabel}>
                            {packageObj.name}
                        </div>
                        <div className={styles.tableHeaderSubLabel}>
                            {record.batchName}
                        </div>
                    </div>
                );
            }
        },
        {
            key: 'centerId',
            dataIndex: 'centerId',
            title: <div className={styles.tableHeader}>
                <div className={styles.tableHeaderLabel}>
                    {formatMsg('center')}
                </div>
            </div>,
            sorter: (a, b) => {
                const a1 = { ...a, centerName: centersMap.get(a.centerId)?.name || '' }
                const b1 = { ...b, centerName: centersMap.get(b.centerId)?.name || '' }
                return Helper.sortStringValue(a1, b1, "centerName")
            },
            render: (text, record) => {

                const centerObj = centersMap.get(record.centerId) || {};

                return (
                    <div className={styles.tableHeader}>
                        <div className={styles.tableHeaderLabel}>
                            {centerObj.name}
                        </div>
                    </div>
                );
            }
        },
        {
            key: 'date',
            dataIndex: 'date',
            title: <div className={styles.tableHeader}>
                <div className={styles.tableHeaderLabel}>
                    {formatMsg('date')}
                </div>
                <div className={styles.tableHeaderSubLabel}>
                    {formatMsg('timeSlot')}
                </div>
            </div>,
            sorter: (a, b) => Helper.sortIntegerValue(a, b, "date"),
            render: (text, record) => {

                return (
                    <div className={styles.tableHeader}>
                        <div className={styles.tableHeaderLabel}>
                            {timeutil.getInstance(record.date).format(Helper.dateFormat(firebase))}
                        </div>
                        <div className={styles.tableHeaderSubLabel}>
                            {timeutil.getInstance(record.startTime).format("hh:mm A")} - {timeutil.getInstance(record.endTime).format("hh:mm A")}
                        </div>
                    </div>
                );
            }
        },
        {
            key: 'attendance',
            dataIndex: 'attendance',
            title: <div className={styles.attendanceHeader}>
                <div className={styles.tableHeaderLabel}>
                    {formatMsg('attendance')}
                </div>
            </div>,
            render: (_, record) => {
                const dateSlotCenterEntityBatchPackageKey = `${timeutil.inFormat(record.date, 'DD-MM-YY')}-$-${record.slotRandomId}-$-${record.centerId}-$-${record.staffId}-$-${record.batchId}-$-${record.packageId}`;
                const displayAttendanceObj = getEntityDisplayAttendanceObj('INSTRUCTOR', dateSlotCenterEntityBatchPackageKey);
                const attendanceStatus: AttendanceStatus = Helper.getAttendanceStatus(displayAttendanceObj);
                return (
                    <div className={styles.attendanceHeader}>
                        {(attendanceStatus === 'CLEARED' || attendanceStatus === 'PENDING') && (
                            <div className={styles.attendanceHeader}>
                                <img
                                    className={styles.actionImg}
                                    src='/images/yellowExclaimation.png'
                                />
                            </div>
                        )}
                        {attendanceStatus === "ABSENT" && (
                            <div className={styles.attendanceHeader}>
                                <img
                                    className={styles.actionImg}
                                    src='/images/absent.png'
                                />
                                {getAbsentNote('INSTRUCTOR', dateSlotCenterEntityBatchPackageKey)}
                            </div>
                        )}
                        {attendanceStatus === "PRESENT" && (
                            <div className={styles.attendanceHeader}>
                                <img
                                    className={styles.actionImg}
                                    src='/images/present.png'
                                />
                                {
                                    getCheckInCheckOutTime(displayAttendanceObj)
                                }
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            key: "actions",
            title: (
                <div className={styles.entityContainer}>
                    {renderBulkAttendanceActions('INSTRUCTOR', 'BULK_INSTRUCTORS',
                        allInstructorsEvents?.some(record =>
                            permissions.hasPermissionForEntity(firebase, permissions.PERMISSIONS.ATTENDANCE_MARK, [record.centerId])
                        )
                    )}
                </div>
            ),
            render: (_, record) => {
                const canMarkAttendance = permissions.hasPermissionForEntity(firebase, permissions.PERMISSIONS.ATTENDANCE_MARK, [record.centerId])
                return (
                    <div className={styles.entityContainer}>
                        {renderIndividualAttendanceActions('INSTRUCTOR', record.staffId, record, canMarkAttendance)}
                    </div>
                );
            },
        }
    ];

    function getStickersList(attendanceStats, attendanceSettings) {
        const stickers = [];
        if (attendanceSettings?.type === "checkin_checkout") {
            stickers.push(
                {
                    imgSrc: "/images/present.png",
                    label: formatMsg('totalPresents'),
                    stickerVal: attendanceStats?.present || 0
                },
                {
                    imgSrc: "/images/absent.png",
                    label: formatMsg('totalAbsent'),
                    stickerVal: attendanceStats?.absent || 0
                },
                {
                    imgSrc: "/images/checkInPending.png",
                    label: formatMsg('pendingCheckin'),
                    stickerVal: attendanceStats?.pendingCheckin || 0
                },
                {
                    imgSrc: "/images/checkOutPending.png",
                    label: formatMsg('pendingCheckout'),
                    stickerVal: attendanceStats?.pendingCheckout || 0
                }
            );
        } else {
            stickers.push(
                {
                    imgSrc: "/images/present.png",
                    label: formatMsg('totalPresents'),
                    stickerVal: attendanceStats?.present || 0
                },
                {
                    imgSrc: "/images/absent.png",
                    label: formatMsg('totalAbsent'),
                    stickerVal: attendanceStats?.absent || 0
                },
                {
                    imgSrc: "/images/yellowExclaimation.png",
                    label: formatMsg('pending'),
                    stickerVal: attendanceStats?.pending || 0
                }
            );
        }
        return stickers;
    }





    const tabs: TabsProps['items'] = [
        {
            key: 'PARTICIPANT',
            label: (firebase?.schoolConfig?.useUserTypeAsDisplayName && firebase?.schoolConfig?.selectedUserTypeName)
                ? firebase?.schoolConfig?.selectedUserTypeName
                : formatMsg('users'),
            children: <EntityAttendance
                entityType={'PARTICIPANT'}
                entityColumns={participantColumns}
                entityData={allParticipantsEvents}
                centersMap={centersMap}
                packagesMap={packagesMap}
                batchesMap={batchesMap}
                usersMap={participantsMap}
                dateRange={participantDateRange}
                setDateRange={setParticipantDateRange}
                selectedRowKeys={participantsBulkActionList}
                setSelectedRowKeys={setParticipantBulkActionList}
                attendanceSettings={participantAttendanceSettings}
                attendanceMap={participantsAttendanceMap}
                editMode={participantEditMode}
                onSubmit={onSubmit}
                onCancel={onCancel}
            />
        },
        {
            key: 'INSTRUCTOR',
            label: formatMsg("instructors"),
            children: <EntityAttendance
                entityType={'INSTRUCTOR'}
                entityColumns={instructorColumns}
                entityData={allInstructorsEvents}
                centersMap={centersMap}
                packagesMap={packagesMap}
                batchesMap={batchesMap}
                usersMap={teachersMap}
                dateRange={instructorDateRange}
                setDateRange={setInstructorDateRange}
                selectedRowKeys={instructorsBulkActionList}
                setSelectedRowKeys={setInstructorBulkActionList}
                attendanceMap={instructorsAttendanceMap}
                attendanceSettings={instructorAttendanceSettings}
                editMode={instructorEditMode}
                onSubmit={onSubmit}
                onCancel={onCancel}
            />
        }
    ];

    return (
        <Box>
            <PageHeaderWrapper>
                <div className='attendance-page-header'>
                    {formatMsg('attendance')}
                </div>
            </PageHeaderWrapper>
            <div>
                {isTeacherLoggedIn
                    ? <Tabs
                        defaultActiveKey='PARTICIPANT'
                        activeKey={activeTab}
                        items={tabs}
                        onChange={(key) => {
                            setActivetab(key);
                            Helper.setUrlParam("tab", key)
                        }}
                    />
                    : <EntityAttendance
                        entityType={'PARTICIPANT'}
                        entityColumns={participantColumns}
                        entityData={allParticipantsEvents}
                        centersMap={centersMap}
                        packagesMap={packagesMap}
                        batchesMap={batchesMap}
                        usersMap={participantsMap}
                        dateRange={participantDateRange}
                        setDateRange={setParticipantDateRange}
                        selectedRowKeys={participantsBulkActionList}
                        setSelectedRowKeys={setParticipantBulkActionList}
                        attendanceMap={participantsAttendanceMap}
                        attendanceSettings={participantAttendanceSettings}
                        editMode={participantEditMode}
                        onSubmit={onSubmit}
                        onCancel={onCancel}
                        accountsMap={accountsMap}
                    />
                }
            </div>
            <AbsentNoteModal
                form={form}
                isAbsentNoteModalOpen={isAbsentNoteModalOpen}
                handleAbsentNoteOk={handleAbsentNoteOk}
                handleAbsentNoteCancel={handleAbsentNoteCancel}
            />
        </Box>
    )
}

export default Attendance